import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import Gamepad2 from 'lucide-react/dist/esm/icons/gamepad-2';
import Headphones from 'lucide-react/dist/esm/icons/headphones';
import Keyboard from 'lucide-react/dist/esm/icons/keyboard';
import Laptop from 'lucide-react/dist/esm/icons/laptop';
import Monitor from 'lucide-react/dist/esm/icons/monitor';
import Smartphone from 'lucide-react/dist/esm/icons/smartphone';
import Tv from 'lucide-react/dist/esm/icons/tv';
import Zap from 'lucide-react/dist/esm/icons/zap';
import { api, fetchApi } from '@/lib/api';
import { HERO_SECTION_CATEGORIES, itemsHrefForTag } from '@/lib/featured-categories';
import { buildCanonicalLink, buildJsonLd, buildMeta } from '@/lib/seo';
import { ssrForPublicOnly } from '@/lib/ssr-utils';
import { FeaturedCategorySection } from './index/-components/featured-category-section';
import { HomeBottomCta } from './index/-components/home-bottom-cta';
import { HomeHero } from './index/-components/home-hero';
import { PriceDropsSection } from './index/-components/price-drops-section';

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  Smartphone: <Smartphone className="h-5 w-5 text-muted-foreground" />,
  Laptop: <Laptop className="h-5 w-5 text-muted-foreground" />,
  Tv: <Tv className="h-5 w-5 text-muted-foreground" />,
  Headphones: <Headphones className="h-5 w-5 text-muted-foreground" />,
  Gamepad2: <Gamepad2 className="h-5 w-5 text-muted-foreground" />,
  Monitor: <Monitor className="h-5 w-5 text-muted-foreground" />,
  Keyboard: <Keyboard className="h-5 w-5 text-muted-foreground" />,
  Zap: <Zap className="h-5 w-5 text-muted-foreground" />,
};

const FEATURED_CATEGORY_IDS = HERO_SECTION_CATEGORIES.map((c) => c.tagId).join(',');

export const Route = createFileRoute('/')({
  ssr: ssrForPublicOnly,
  head: () => ({
    meta: buildMeta({
      title: 'Pricedrop - מעקב מחירים KSP | היסטוריית מחירים והשוואה',
      description: 'עקבו אחרי מחירי מוצרי KSP, השוו היסטוריית מחירים ומצאו את הזמן הנכון לקנות. התראות מחיר חינם ותוסף דפדפן.',
      canonicalUrl: '/',
    }),
    links: [buildCanonicalLink('/')],
    scripts: [
      buildJsonLd({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Pricedrop',
        url: 'https://pricedrop.co.il',
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: 'https://pricedrop.co.il/items?query={search_term_string}',
          },
          'query-input': 'required name=search_term_string',
        },
      }),
      buildJsonLd({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Pricedrop',
        url: 'https://pricedrop.co.il',
        logo: 'https://pricedrop.co.il/icon-512.png',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          url: 'https://pricedrop.co.il/contact',
          availableLanguage: ['Hebrew', 'English'],
        },
      }),
    ],
  }),
  loader: async () => {
    try {
      const [featured, searchResult] = await Promise.all([
        fetchApi(
          api.api.items.featured({ categoryIds: FEATURED_CATEGORY_IDS }).get(),
        ),
        fetchApi(
          api.api.items.search.post({
            afterDropOnly: true,
            sort: 'updated-desc',
            page: 1,
          }),
        ),
      ]);
      return {
        featured: featured as unknown as Record<number, FeaturedItem[]>,
        priceDrops: (searchResult as unknown as SearchResult).items.slice(0, 4),
      };
    } catch {
      return { featured: null, priceDrops: null };
    }
  },
  component: HomePage,
});

interface FeaturedItem {
  uin: number;
  name: string;
  uinsql: string;
  brandName: string;
  price: number | null;
  highestPrice: number;
  lowestPrice: number;
  meanPrice: number;
  previousPrice: number;
}

interface SearchResult {
  items: FeaturedItem[];
  hasNextPage: boolean;
}

function HomePage() {
  const loaderData = Route.useLoaderData();

  const { data: featured, isLoading: isLoadingFeatured } = useQuery({
    queryKey: ['home-featured', FEATURED_CATEGORY_IDS],
    queryFn: async () => {
      const result = await fetchApi(
        api.api.items.featured({ categoryIds: FEATURED_CATEGORY_IDS }).get(),
      );
      return result as unknown as Record<number, FeaturedItem[]>;
    },
    initialData: loaderData?.featured ?? undefined,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: priceDrops, isLoading: isLoadingDrops } = useQuery({
    queryKey: ['home-price-drops'],
    queryFn: async () => {
      const result = await fetchApi(
        api.api.items.search.post({
          afterDropOnly: true,
          sort: 'updated-desc',
          page: 1,
        }),
      );
      return (result as unknown as SearchResult).items.slice(0, 4);
    },
    initialData: loaderData?.priceDrops ?? undefined,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return (
    <div className="flex flex-col gap-16 md:gap-20 pb-16">
      <HomeHero />

      <PriceDropsSection items={priceDrops} loading={isLoadingDrops} />

      {HERO_SECTION_CATEGORIES.map((cat) => (
        <FeaturedCategorySection
          key={cat.tagId}
          title={cat.label()}
          icon={CATEGORY_ICONS[cat.iconName]}
          items={featured?.[cat.tagId]}
          viewAllHref={itemsHrefForTag(cat.tagId)}
          loading={isLoadingFeatured}
        />
      ))}

      <HomeBottomCta />
    </div>
  );
}
